/** Pure helpers for Web Push reminders (no DOM, no network). */

/** Decodes a base64url VAPID key into the bytes PushManager.subscribe expects. */
export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Encodes raw key bytes (from PushSubscription.getKey) as unpadded base64url. */
export function bytesToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  let binary = '';
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const DEFAULT_REMINDER_TIME = '20:00';

/** "HH:MM" from a Postgres time ("20:00:00", "19:54:04.28") or an <input type="time"> value. */
export function normalizeReminderTime(value: string | null | undefined): string {
  const match = /^(\d{1,2}):(\d{2})/.exec(value ?? '');
  if (!match) return DEFAULT_REMINDER_TIME;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return DEFAULT_REMINDER_TIME;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export type PushSupport =
  /** Web Push can be used here. */
  | 'ready'
  /** iPhone/iPad Safari tab: push only works from the home-screen app (iOS 16.4+). */
  | 'ios-needs-home-screen'
  /** Browser or WebView without Web Push. */
  | 'unsupported';

export interface PushEnvironment {
  hasServiceWorker: boolean;
  hasPushManager: boolean;
  hasNotification: boolean;
  isIos: boolean;
  isStandalone: boolean;
}

export function pushSupport(env: PushEnvironment): PushSupport {
  if (env.isIos && !env.isStandalone) return 'ios-needs-home-screen';
  if (env.hasServiceWorker && env.hasPushManager && env.hasNotification) return 'ready';
  return 'unsupported';
}
